import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import AuthPage from "@/pages/AuthPage";
import AcceptInvitationPage from "@/pages/AcceptInvitationPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Dashboard from "@/pages/Dashboard";
import Troupeau from "@/pages/Troupeau";
import Depenses from "@/pages/Depenses";
import Transactions from "@/pages/Transactions";
import Stock from "@/pages/Stock";
import Naissances from "@/pages/Naissances";
import Rapports from "@/pages/Rapports";
import Utilisateurs from "@/pages/Utilisateurs";
import Parametres from "@/pages/Parametres";
import Alertes from "@/pages/Alertes";
import TemplatesEmail from "@/pages/TemplatesEmail";
import HistoriqueTaches from "@/pages/HistoriqueTaches";
import CIStatus from "@/pages/CIStatus";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { SettingsProvider, useSettings } from "@/contexts/SettingsContext";
import { ThemeProvider } from "@/contexts/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Setup axios interceptor for auth
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
    
    // Settings will be fetched automatically by SettingsContext when token is available
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-slate-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <SettingsProvider>
    <ThemeProvider>
      <div className="App">
        <Toaster position="top-right" richColors />
        {(process.env.REACT_APP_ENV === 'DEV' || window.location.hostname.startsWith('dev.')) && (
          <div className="bg-red-600 text-white text-center text-xs font-bold py-1 tracking-wider z-50 relative" data-testid="dev-banner">
            ENVIRONNEMENT DE DÉVELOPPEMENT
          </div>
        )}
        {BACKEND_URL?.includes('preview.emergentagent.com') && (
          <div className="bg-amber-600 text-white text-center text-xs font-bold py-1 tracking-wider z-50 relative" data-testid="preview-banner">
            ENVIRONNEMENT DE PREVIEW
          </div>
        )}
        <BrowserRouter>
          <Routes>
          {!isAuthenticated ? (
            <>
              <Route path="/login" element={<AuthPage onLogin={handleLogin} />} />
              <Route path="/register" element={<Navigate to="/login" replace />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/accept-invitation/:token" element={<AcceptInvitationPage onLogin={handleLogin} />} />
              <Route path="/auth" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          ) : (
            <Route element={<Layout user={user} onLogout={handleLogout} />}>
              <Route path="/" element={<Dashboard user={user} />} />
              <Route path="/troupeau" element={<Troupeau user={user} />} />
              <Route path="/depenses" element={<Depenses user={user} />} />
              <Route path="/transactions" element={<Transactions user={user} />} />
              <Route path="/stock" element={<Stock user={user} />} />
              <Route path="/naissances" element={<Naissances user={user} />} />
              <Route path="/rapports" element={<Rapports user={user} />} />
              <Route path="/alertes" element={
                user?.role === 'admin' ? <Alertes user={user} /> : <Navigate to="/" replace />
              } />
              <Route path="/templates-email" element={
                user?.role === 'admin' ? <TemplatesEmail user={user} /> : <Navigate to="/" replace />
              } />
              <Route path="/historique-taches" element={
                user?.role === 'admin' ? <HistoriqueTaches user={user} /> : <Navigate to="/" replace />
              } />
              <Route path="/utilisateurs" element={
                user?.role === 'admin' ? <Utilisateurs user={user} /> : <Navigate to="/" replace />
              } />
              <Route path="/parametres" element={<Parametres user={user} />} />
              <Route path="/ci-status" element={
                BACKEND_URL?.includes('preview.emergentagent.com') 
                  ? <CIStatus user={user} /> 
                  : <Navigate to="/" replace />
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          )}
          </Routes>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  </SettingsProvider>
  );
}

export default App;